import { apiClient } from '@/lib/api/client'

/**
 * SOLY-TPL-001 -- the organization profile, as it comes out on the documents.
 *
 * Until this feature the templates carried Kitchy's identity hard-coded, so an
 * organization with an empty profile still produced a plausible invoice -- someone
 * else's. Now that the document says what the profile says, an incomplete profile
 * produces a document that lies by omission.
 */
export interface OrganizationProfileDto {
  organizationId: string
  name: string
  street: string
  number: string
  box: string | null
  postalCode: string
  city: string
  country: string
  vatNumber: string
  email: string
  phone: string
  logoUrl: string | null
  website: string | null
  bankAccounts: string[]
  /** Field codes still missing before a document can be issued. Empty means complete. */
  missingForDocuments: OrganizationProfileGap[]
  isCompleteForDocuments: boolean
}

/**
 * The codes the backend returns. Codes, not sentences: the backend does not know the
 * user's language, so the wording lives here.
 */
export type OrganizationProfileGap = 'name' | 'address' | 'vat_number' | 'bank_account'

/** What the user has to go and fill in, in their own words. */
export const PROFILE_GAP_LABELS: Record<OrganizationProfileGap, string> = {
  name: 'le nom de la société',
  address: 'l’adresse du siège',
  vat_number: 'le numéro de TVA',
  bank_account: 'le compte bancaire (IBAN)',
}

/**
 * Worst gap first. The IBAN opens the list: an invoice with no account is a demand
 * for payment with no way to pay.
 */
const GAP_ORDER: OrganizationProfileGap[] = ['bank_account', 'vat_number', 'address', 'name']

export function sortGaps(gaps: OrganizationProfileGap[]): OrganizationProfileGap[] {
  return [...gaps].sort((a, b) => GAP_ORDER.indexOf(a) - GAP_ORDER.indexOf(b))
}

/** Readable enumeration: "le compte bancaire (IBAN), le numéro de TVA et l’adresse du siège". */
export function describeGaps(gaps: OrganizationProfileGap[]): string {
  const labels = sortGaps(gaps)
    .map((gap) => PROFILE_GAP_LABELS[gap])
    .filter(Boolean)

  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`
}

export async function getOrganizationProfile(organizationId: string): Promise<OrganizationProfileDto> {
  return apiClient.get<OrganizationProfileDto>(`/organizations/${organizationId}/profile`)
}

/**
 * What a PUT sends. Deliberately NOT the DTO: the response carries the gaps and the
 * organization id, which are computed, and sending them back would suggest they can
 * be set.
 */
export interface UpdateOrganizationProfileRequest {
  street: string
  number: string
  box: string | null
  postalCode: string
  city: string
  country: string
  vatNumber: string
  email: string
  phone: string
  logoUrl: string | null
  website: string | null
  bankAccounts: string[]
}

/**
 * ⚠️ The backend REPLACES the whole profile: every field travels, or it is wiped
 * (OrganizationsController.UpdateProfileAsync assigns each one without a null check).
 * So the form always posts its complete state, never a patch.
 */
export async function updateOrganizationProfile(
  organizationId: string,
  request: UpdateOrganizationProfileRequest
): Promise<OrganizationProfileDto> {
  return apiClient.put<OrganizationProfileDto>(`/organizations/${organizationId}/profile`, request)
}

/**
 * The field names ASP.NET puts in a ValidationProblemDetails, in the user's words.
 *
 * The API answers a rejected profile with `{ title, status, errors: { LogoUrl: [...] } }`.
 * `apiClient` builds its message from `error.message` / `message` / `error` — none of
 * which that payload carries — so every validation failure reached the screen as
 * "Une erreur est survenue". Naming the field is the entire point of surfacing the
 * server's answer, so the parsing has to happen here.
 */
const FIELD_LABELS: Record<string, string> = {
  street: 'la rue',
  number: 'le numéro',
  box: 'la boîte',
  postalcode: 'le code postal',
  city: 'la commune',
  country: 'le pays (deux lettres, ex. BE)',
  vatnumber: 'le numéro de TVA',
  email: "l'e-mail de la société",
  phone: 'le téléphone',
  logourl: 'le logo',
  website: 'le site web',
  bankaccounts: 'le compte bancaire',
}

/**
 * Turns a rejected save into a sentence that names the offending fields, or null when
 * the response carries nothing usable.
 */
export function describeValidationError(response: unknown): string | null {
  const data = (response as { data?: { errors?: Record<string, string[]> } } | undefined)?.data
  const errors = data?.errors
  if (!errors || typeof errors !== 'object') return null

  const fields = Object.keys(errors)
    .map(k => FIELD_LABELS[k.toLowerCase()] ?? k)
    .filter(Boolean)
  if (fields.length === 0) return null

  const first = Object.values(errors)[0]?.[0]
  const detail = typeof first === 'string' && first.trim() ? ` ${first.trim()}` : ''
  return `Le serveur a refusé ${fields.join(', ')}.${detail}`
}
